<script setup>
// core
import {useStore} from "vuex";
import {computed} from "vue";

// components
import CallNumberView from "@/components/Telephony/CallNumberView.vue";

// icons
import RefreshApi from "@/components/Telephony/icons/RefreshApi.vue";

const store = useStore()
const number = computed(() => store.state.dialogParams?.number)
const info = computed(() => store.state.dialogParams?.info)

const webhook = computed(() => info.value?.webhooks?.[0]?.url)

const updateApiKey = () => {
    store.commit("setNextDialog", "NumberSettingsDialog");
    store.commit("setDialog", "ApiKeyDialog");
}

const actionWebhookMenu = {
    add: () => {
        store.commit("setTypeOfDialogAction", "add");
        store.commit("setNextDialog", "NumberSettingsDialog");
        store.commit("setDialog", "WebhookDialog");
    },
    edit: () => {
        store.commit("setTypeOfDialogAction", "edit");
        store.commit("setNextDialog", "NumberSettingsDialog");
        store.commit("setDialog", "WebhookDialog");
    },
    delete: () => {
        store.commit("setTypeOfDialogAction", "delete");
        store.commit("setNextDialog", "NumberSettingsDialog");
        store.commit("setDialog", "WebhookDelete");
    },
};

const addSip = () => {
    store.commit("setTypeOfDialogAction", "");
    store.commit("setNextDialog", "NumberSettingsDialog");
    store.commit("setDialog", "SelectSipDialog");
}
</script>

<template>
    <slot name="header_close"/>
    <div class="wrapper">
        <h3 class="title">
            {{ $t('telephony.connected_numbers.settings.title') }}
            <div class="number-block">
                <CallNumberView :number="number"/>
            </div>
        </h3>

        <div class="content mt-4">
            <!-- APIKEY -->
            <div class="setting_block wide_block">
                <div class="block_title">
                    <h4>{{ $t('telephony.connected_numbers.settings.api_key') }}</h4>
                </div>
                <div class="action_container">
                    <button class="inside_block text" @click="updateApiKey">
                        <RefreshApi class="icon"/>
                        {{ $t('common.update') }}
                    </button>
                </div>
            </div>

            <!-- WEBHOOK -->
            <div class="setting_block wide_block">
                <div class="block_title">
                    <h4>Webhook URL</h4>
                </div>

                <ul class="information" v-show="webhook">
                    <li class="info_item" :title="`Voip WEBHOOK URL: ${webhook}`">
                        <b class="item_title">Voip</b>
                        <i class="text_overflow">{{ webhook }}</i>
                    </li>
                </ul>

                <div class="action_container multi_action">
                    <button
                        v-if="!info.webhooks.length"
                        class="inside_block text"
                        @click="actionWebhookMenu.add()"
                    >
                        <i class="bi bi-plus-lg"></i>
                        {{ $t('common.add') }}
                    </button>
                    <template v-else>
                        <button
                            class="inside_block"
                            :title="$t('common.edit')"
                            @click="actionWebhookMenu.edit()"
                        >
                            <i class="icon fa fa-pencil"></i>
                        </button>
                        <button
                            class="inside_block"
                            :title="$t('common.delete')"
                            @click="actionWebhookMenu.delete()"
                        >
                            <i class="icon fa fa-trash"></i>
                        </button>
                    </template>
                </div>
            </div>

            <!-- SIP -->
            <div class="setting_block wide_block">
                <div class="block_title">
                    <h4>{{ $t('telephony.connected_numbers.settings.connected_sip') }}</h4>
                    <span v-if="info.extensions?.length" class="count">{{ info.extensions.length }}</span>
                </div>
                <div class="action_container">
                    <button class="inside_block text" @click="addSip">
                        <i class="bi bi-list-ul"></i>
                        {{ $t('common.select') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
// the whole window repeats SettingsNumberDialog, only the blocks differ
.wrapper {
    padding: 20px 28px 42px;
    max-width: 750px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 7px;
    background-color: white;
    text-align: center;
}

.title {
    font-size: 20px;
    text-align: center;
    font-family: Gilroy;
    font-weight: 500;
    text-transform: uppercase;
    color: var(--text-color);
}

.number-block {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 10px;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color);
}

.content {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

.action_container {
    display: flex;
    justify-content: center;
}

.multi_action {
    width: fit-content;
    gap: 10px;
}

.setting_block {
    height: 50px;
    min-width: 240px;
    width: calc(50% - 5px);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 10px;
    border-radius: 7px;
    margin-bottom: 20px;
    border: 1px solid var(--general-color);
    background-color: var(--block-color);
    transition: all 0.7s ease;

    &:hover {
        background-color: var(--block-second-color);
    }
}

.wide_block {
    width: 100%;
    justify-content: space-between;

    .block_title {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: none;
        gap: 10px;
        width: fit-content;
    }
}

.block_title {
    flex: 1;
    text-align: start;
    font-family: "Gilroy";
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    line-height: 24px;
    color: rgba(0, 0, 0, 0.6);
}

// the trunk count is bracketed like the tag of a messenger number
.count {
    font-size: 14px;
    font-weight: 500;
    line-height: 19px;
    letter-spacing: 0.05rem;
    text-transform: none;
    color: var(--text-third-color);

    &::before,
    &::after {
        color: var(--border-primary-color);
    }

    &::before {
        content: "[";
        margin-right: 4px;
    }

    &::after {
        content: "]";
        margin-left: 4px;
    }
}

.information {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: 14px;
    line-height: 19px;
    font-size: 14px;
    overflow: hidden;
}

.info_item {
    cursor: pointer;
    display: flex;
    gap: 6px;
    width: 100%;
    min-width: 0;
    color: var(--text-third-color);
    align-items: center;
}

// the url scrolls behind a fade, as the password in the sip cards
.text_overflow {
    flex: 1 1 0;
    min-width: 0;
    overflow-x: auto;
    white-space: nowrap;

    // html sets Figtree, which the project does not ship
    font-family: "Gilroy", sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    letter-spacing: 0.05rem;
    color: var(--text-third-color);

    // the scrollbar eats 5px inside the row and lifts the url off the label
    // baseline; wheel and trackpad scrolling still work
    scrollbar-width: none;
    -ms-overflow-style: none;

    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);
    mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);

    &::-webkit-scrollbar {
        display: none;
    }
}

.item_title {
    flex: 0 0 auto;
    display: flex;
    font-size: 12px;
    font-weight: 500;
    line-height: 19px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--border-primary-color);

    &:after {
        content: ":";
    }
}

.inside_block {
    position: relative;
    min-width: 35px;
    width: 35px;
    height: 35px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 9px;
    border: 1px solid var(--general-color);
    background-color: #ffffff;

    transition: 0.25s;

    &.text {
        width: 140px;
        min-width: 140px;
        padding: 0 16px;
        gap: 10px;
        color: var(--general-color);
        font-family: "Gilroy", sans-serif;
        font-size: 14px;
        font-weight: 600;
        line-height: 17px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        white-space: nowrap;

        i {
            font-size: 16px;
        }

        .icon {
            flex: 0 0 auto;
            width: 22px;
            height: 22px;
        }
    }

    .icon {
        width: 20px;
        height: 20px;
        font-size: 18px;
        fill: var(--general-color);
        color: var(--general-color);
        transition: 0.25s;
    }

    &:hover {
        cursor: pointer;
        background-color: var(--general-color);
        transition: 0.25s;

        &.text {
            color: white;
        }

        .icon {
            fill: white;
            color: white;
        }
    }
}

@media (max-width: 570px) {
    .setting_block {
        width: 100%;
    }
}

@media (max-width: 420px) {
    .information {
        line-height: 22px;
    }

    .text_overflow {
        display: none;
    }

    .item_title {
        margin: auto;
        color: var(--general-color);
        transition: 0.5s;

        &:after {
            content: "";
        }

        &:hover {
            color: var(--text-light-color);
            transition: 0.5s;
        }
    }
}
</style>
